import {ApolloError} from '@apollo/client'
import Plus from '@rsuite/icons/legacy/Plus'
import Trash from '@rsuite/icons/legacy/Trash'
import React, {useContext, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {AppContext} from '@dudagroup/editor/lib/client/control/appContext'
import {
  Button,
  ButtonToolbar,
  Form,
  IconButton,
  Input,
  Loader,
  Notification,
  SelectPicker,
  Stack,
  toaster,
} from 'rsuite'
import {
  ImportNotificationLanguage,
  useImportNotificationSettingsQuery,
  useUpdateImportNotificationSettingsMutation,
} from '../../../api'

interface Recipient {
  email: string
  language: ImportNotificationLanguage
}

const LANGUAGE_OPTIONS = [
  {label: 'Deutsch', value: ImportNotificationLanguage.De},
  {label: 'English', value: ImportNotificationLanguage.En},
]

function notify(type: 'success' | 'error', header: string) {
  toaster.push(<Notification closable type={type} header={header} duration={5000} />, {
    placement: 'topEnd',
  })
}

// The CMS shell and custom views can use separate i18next instances, so take the
// language from AppContext to follow the menu's language switch.
function useEmailNotificationsTranslation() {
  const [{uiLanguage}] = useContext(AppContext)
  const {i18n} = useTranslation()
  return i18n.getFixedT(uiLanguage, undefined, 'emailNotifications')
}

// Also drops Apollo's __typename, which the mutation input does not accept.
function normalizeRecipients(recipients: readonly Recipient[]): Recipient[] {
  return recipients
    .map(({email, language}) => ({email: email.trim().toLowerCase(), language}))
    .filter(({email}) => email)
}

export function CustomViewEmailNotifications() {
  const t = useEmailNotificationsTranslation()
  const {data, loading} = useImportNotificationSettingsQuery({fetchPolicy: 'network-only'})
  const [updateSettings, {loading: saving}] = useUpdateImportNotificationSettingsMutation()
  const [recipients, setRecipients] = useState<Recipient[]>([])
  const [sendingTest, setSendingTest] = useState(false)

  const savedRecipients = data?.extensions.importNotificationSettings.recipients
  useEffect(() => {
    if (savedRecipients) setRecipients(normalizeRecipients(savedRecipients))
  }, [savedRecipients])

  function updateRecipient(index: number, change: Partial<Recipient>) {
    setRecipients(
      recipients.map((recipient, i) => (i === index ? {...recipient, ...change} : recipient))
    )
  }

  async function onSave() {
    try {
      const {data} = await updateSettings({
        variables: {recipients: normalizeRecipients(recipients)},
      })
      setRecipients(
        normalizeRecipients(data?.extensions.updateImportNotificationSettings.recipients ?? [])
      )
      notify('success', t('saved'))
    } catch (error) {
      notify('error', (error as ApolloError).message)
    }
  }

  // Sent by the editor server (it holds the SMTP credentials); the session
  // token proves to the API behind it that an editor user is asking.
  async function onSendTest() {
    setSendingTest(true)
    try {
      const token = localStorage.getItem('sessionToken')
      const response = await fetch('/import-notifications/test', {
        method: 'POST',
        headers: token ? {Authorization: `Bearer ${token}`} : {},
      })
      const {code, detail} = (await response.json()) as {code?: string; detail?: string}
      notify(
        response.ok ? 'success' : 'error',
        t([`test.${code}`, 'test.unknown'], {detail, interpolation: {escapeValue: false}})
      )
    } catch (error) {
      notify('error', `${error}`)
    } finally {
      setSendingTest(false)
    }
  }

  // The test goes to the saved list, so unsaved edits would make it misleading.
  const hasUnsavedChanges =
    JSON.stringify(normalizeRecipients(recipients)) !==
    JSON.stringify(normalizeRecipients(savedRecipients ?? []))

  if (loading) return <Loader center />

  return (
    <div>
      <h2>{t('title')}</h2>
      <br />
      <h4>{t('failedImports')}</h4>
      <p>{t('failedImportsDescription')}</p>
      <br />
      <Form>
        {recipients.map((recipient, index) => (
          <Form.Group key={index}>
            <Stack spacing={8}>
              <Input
                style={{width: 320}}
                value={recipient.email}
                onChange={(email) => updateRecipient(index, {email})}
                placeholder="name@example.com"
              />
              <SelectPicker
                placement="autoVerticalStart"
                preventOverflow
                data={LANGUAGE_OPTIONS}
                value={recipient.language}
                onChange={(language) => language && updateRecipient(index, {language})}
                searchable={false}
                cleanable={false}
              />
              <IconButton
                icon={<Trash />}
                appearance="subtle"
                aria-label={t('removeRecipient')}
                onClick={() => setRecipients(recipients.filter((_, i) => i !== index))}
              />
            </Stack>
          </Form.Group>
        ))}
        <Form.Group>
          <IconButton
            icon={<Plus />}
            onClick={() =>
              setRecipients([...recipients, {email: '', language: ImportNotificationLanguage.De}])
            }>
            {t('addRecipient')}
          </IconButton>
        </Form.Group>
        <Form.Group>
          <ButtonToolbar>
            <Button appearance="primary" loading={saving} onClick={() => void onSave()}>
              {t('save')}
            </Button>
            <Button
              loading={sendingTest}
              disabled={hasUnsavedChanges || !savedRecipients?.length}
              onClick={() => void onSendTest()}>
              {t('sendTest')}
            </Button>
          </ButtonToolbar>
        </Form.Group>
      </Form>
    </div>
  )
}
